// @ts-nocheck

import React, { useLayoutEffect, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { ModalFormat, ModalContentSpacing } from '../../constants/ui/modal';
import { useViewportContext } from '../../contexts/ViewportContext';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import { __ } from '../../i18n/l10n';
import { ZIndices } from '../../styled-components/dark/common';
import { KeyCodes } from '../../types/keyboard';
import ExitButton from '../ExitButton';
import { withFixedContent } from '../ModalFixed';
import { withFooter } from '../ModalFooter';
import { withHeader } from '../ModalHeader';

import {
  DesktopWrapper,
  Header,
  Fixed,
  Footer,
  Overlay,
  SafeAreaWrapper,
  StyledScrollView,
  VerticalCenter,
  Wrapper,
  ScrollableContent,
} from './Modal.style';

const FloatingBackButton = styled.div`
  color: var(--Modal-backButtonTextColor, #fff);
  position: absolute;
  padding: 0.5em 1.2em 0.6em 1em;
  width: fit-content;
  background: var(--Modal-backButtonBackground, #000);
  border-bottom-right-radius: 1.2em;
  font-weight: bold;
  opacity: var(--Modal-backButtonOpacity, 0.8);
  cursor: pointer;
  transition: none 150ms ease-in-out;
  transition-property: opacity, font-size, filter;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  font-size: 1.1em;

  &:active {
    font-size: 1em;
    opacity: 1;
    filter: brightness(105%);
  }

  ${!Target.__MOBILE__
    ? css`
        &:hover {
          opacity: 1;
          filter: brightness(110%);
        }
      `
    : css``}

  ${({ $zIndex }) => css`
    z-index: ${$zIndex};
  `}
`;

type ModalContextProps = {
  setOnBackButtonClick?: (onBackButtonClick: (() => void) | null) => void;
};

export const ModalContext = React.createContext<ModalContextProps>({});

export const Modal = (props) => {
  const {
    backgroundColor,
    children,
    closeOnEsc,
    closeOnOutsideClick,
    format = ModalFormat.DESKTOP,
    fullHeight,
    hasCloseButton,
    isCloseButtonInside = false,
    hasOverlay,
    hasBorderRadius,
    hasNavBarSpacing,
    isGrid,
    onClose,
    renderHeader,
    renderFooter,
    renderFixed,
    fixedStyle,
    style,
    contentSpacing = ModalContentSpacing.MEDIUM,
    useViewportSize,
    zIndex = ZIndices.modal,
    withBorder = false,
    hideOverlay = false,
    disableScrollView = false,
    blockHeight = false,
    noOverflowHidden = false,
    contentScrollable = false,
    forcedHeight,
    forbidBackgroundScrolling = false,
    ...rest
  } = props;
  const MOUSE_CLICK_THRESHOLD = 60;
  const modalRef = useRef(null);
  const childrenContent = useRef(null);
  const [state, setState] = useState({ width: 0, height: 0 });

  const [onBackButtonClick, setOnBackButtonClick] = useState(null);

  const { isPipClicked } = usePictureInPicture();
  const { fontSize, width: viewportWidth } = useViewportContext();

  const desktopModalFormat = [ModalFormat.XSMALL, ModalFormat.SMALL, ModalFormat.DESKTOP];

  const isDesktopModalFormat = desktopModalFormat.indexOf(format) !== -1;
  const mousePos = useRef({ x: 0, y: 0 });
  const handleMouseDown = useCallback((e: MouseEvent) => {
    mousePos.current = { x: e.pageX, y: e.pageY };
  }, []);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEsc && e.code === KeyCodes.ESCAPE) {
        onClose && onClose();
      }
    },
    [onClose, closeOnEsc],
  );

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (closeOnOutsideClick) {
        const { target } = e;

        let currNode = target;

        while (currNode) {
          if (currNode?.classList?.contains?.('modal-clickable-elements')) {
            return;
          }
          currNode = currNode.parentNode;
        }

        if (target instanceof Node && modalRef.current && !modalRef.current.contains(target)) {
          // $FlowFixMe: fix the Node flow type error
          // prevent default only when we click outside AND the pip is not clicked otherwhise the PiP controls doesn't work properly
          if (!isPipClicked(target)) {
            e.preventDefault();
            e.stopPropagation();
          }

          const { pageX, pageY } = e;

          // close the modal only when we click outside AND the pip is not clicked
          if (
            Math.abs(mousePos.current.x - pageX) < MOUSE_CLICK_THRESHOLD &&
            Math.abs(mousePos.current.y - pageY) < MOUSE_CLICK_THRESHOLD &&
            !isPipClicked(target)
          ) {
            onClose && onClose();
          }
        }
      }
    },
    [closeOnOutsideClick, isPipClicked, onClose],
  );

  const computeBounds = useCallback(() => {
    const { width, height } = childrenContent.current.getBoundingClientRect();

    setState(() => ({
      width,
      height,
    }));
  }, []);

  useLayoutEffect(() => {
    computeBounds();
    window.addEventListener('resize', computeBounds);
    window.addEventListener('orientationchange', computeBounds);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('click', handleOutsideClick, true);

    return () => {
      window.removeEventListener('resize', computeBounds);
      window.removeEventListener('orientationchange', computeBounds);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('click', handleOutsideClick, true);
    };
  }, [computeBounds, handleKeydown, handleMouseDown, handleOutsideClick]);

  useEffect(() => {
    if (forbidBackgroundScrolling) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (forbidBackgroundScrolling) {
        document.body.style.overflow = '';
      }
    };
  }, [forbidBackgroundScrolling]);

  const ModalContent = (
    <>
      {hasOverlay ? (
        <Overlay data-testid="overlay" $zIndex={typeof zIndex === 'number' ? zIndex - 1 : undefined} />
      ) : null}
      <Wrapper
        ref={modalRef}
        $backgroundColor={backgroundColor}
        $hasBorderRadius={hasBorderRadius}
        $hasNavBarSpacing={hasNavBarSpacing}
        $format={format}
        $isGrid={isGrid}
        $zIndex={zIndex}
        $noOverflowHidden={noOverflowHidden}
        {...rest}
        style={{
          ...style,
          fontSize: Target.__MOBILE__ || useViewportSize ? fontSize : 'initial',
          lineHeight: 'normal',
        }}
      >
        <SafeAreaWrapper $format={format} ref={childrenContent}>
          {renderHeader ? <Header>{renderHeader()}</Header> : null}
          {disableScrollView ? (
            contentScrollable ? (
              <ScrollableContent>{React.cloneElement(children, state)}</ScrollableContent>
            ) : (
              React.cloneElement(children, state)
            )
          ) : (
            <StyledScrollView contentSpacing={contentSpacing} blockHeight={blockHeight}>
              {React.cloneElement(children, state)}
            </StyledScrollView>
          )}
          {renderFooter ? <Footer>{renderFooter()}</Footer> : null}
          {renderFixed ? <Fixed $fixedStyle={fixedStyle}>{renderFixed()}</Fixed> : null}
        </SafeAreaWrapper>
      </Wrapper>
      {hasCloseButton ? (
        <ExitButton
          isCloseButtonInside={isCloseButtonInside}
          zIndex={typeof zIndex === 'number' ? zIndex + 1 : zIndex}
          onClick={onClose}
        />
      ) : null}
    </>
  );

  const providedState = useMemo(
    () => ({
      ...state,
      setOnBackButtonClick,
    }),
    [state],
  );

  return (
    <ModalContext.Provider value={providedState}>
      {isDesktopModalFormat ? (
        <VerticalCenter $zIndex={zIndex} $hideOverlay={hideOverlay}>
          <DesktopWrapper
            $fullHeight={fullHeight}
            $forcedHeight={forcedHeight}
            $format={format}
            $viewportWidth={viewportWidth}
            $withBorder={withBorder}
            $zIndex={zIndex}
          >
            {onBackButtonClick ? (
              <FloatingBackButton
                className="modal-clickable-elements"
                onClick={onBackButtonClick}
                $zIndex={typeof zIndex === 'number' ? zIndex + 1 : undefined}
              >
                {__('Back')}
              </FloatingBackButton>
            ) : null}

            {ModalContent}
          </DesktopWrapper>
        </VerticalCenter>
      ) : (
        ModalContent
      )}
    </ModalContext.Provider>
  );
};

export default withFixedContent(withFooter(withHeader(Modal)));
